import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Calendar, Users, Clock, Shield, LogOut, Gift, History, BarChart3, UserCog } from 'lucide-react';
import CalendarView from './CalendarView';
import RequestsView from './RequestsView';
import TeamView from './TeamView';
import ApprovalsView from './ApprovalsView';
import ManageDaysView from './ManageDaysView';
import UserInfoBar from './UserInfoBar';
import RequestModal from './RequestModal';
import { getUsedDaysByLeaveYear, getLeaveYear } from '../utils/vacationUtils';
import HistoryView from './HistoryView';
import YearlyBalanceView from './YearlyBalanceView'; 
import FeedbackWidget from './FeedbackWidget';
import AdminToolsView from './AdminToolsView';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ─── Add user IDs here to grant read-only access to Team, History & Yearly Balance tabs ───
const VIEWER_USER_IDS = [2, 21, 35, 83, 210];

// ─── Subset of viewer-privilege users who may also create/edit/deactivate
// employee records (name, email, total days only — not role or manager
// assignment). Must match USER_ADMIN_IDS in server.js. ───
const USER_ADMIN_IDS = [2, 19, 21, 40, 210];

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

const Dashboard = ({ 
  currentUser, 
  vacationRequests, 
  userDatabase,
  departments,
  holidays,
  annualBalances = [], 
  onLogout, 
  onRequestSubmit, 
  onRequestDecision,
  onReloadUsers
}) => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedLeaveYears, setSelectedLeaveYears] = useState([]); // empty = current year
  const tabContainerRef = useRef(null);
  const tabRefs = useRef([]);
  const isScrolling = useRef(false);
  const currentLeaveYear = getLeaveYear(new Date());

  const getCurrentCycleAllowance = (balances, userId) => {
    if (!balances || !Array.isArray(balances) || balances.length === 0) return 0;

    const pivotedEntry = balances.find(b => Number(b.userId || b.user_id) === Number(userId));
    const yearKey = `balance_${currentLeaveYear}`;
    if (pivotedEntry && pivotedEntry[yearKey] !== undefined) {
      return Number(pivotedEntry[yearKey]);
    }

    const rawEntry = balances.find(b => Number(b.cycle_start_year || b.year) === currentLeaveYear);
    if (rawEntry && rawEntry.total_allowed !== undefined) {
      return Number(rawEntry.total_allowed);
    }

    return 0;
  };

  const usedDaysInCurrentCycle = useMemo(() => {
    return getUsedDaysByLeaveYear(currentUser.id, vacationRequests, currentLeaveYear, holidays);
  }, [currentUser.id, vacationRequests, holidays, currentLeaveYear]);
  
  const remainingDays = useMemo(() => {
    const currentAllowance = getCurrentCycleAllowance(annualBalances, currentUser.id);
    return Math.max(0, currentAllowance - usedDaysInCurrentCycle);
  }, [currentUser.id, usedDaysInCurrentCycle, annualBalances]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const pendingApprovals = useMemo(() => {
    if (currentUser.role !== 'manager' || !vacationRequests || !Array.isArray(vacationRequests)) {
      return [];
    }
    const managerLevel = currentUser.managerLevel || currentUser.manager_level || 1;
    const filtered = vacationRequests.filter(req => {
      if (managerLevel === 2) {
  const userId = req.user_id || req.userId;
  const user = userDatabase.find(u => u.id === userId);
  if (!user) return false;
  const userManagerId = user.manager_id || user.managerId;
  const isDirectReport = userManagerId === currentUser.id;

  return (req.status === 'pending' && isDirectReport) || req.status === 'manager1_approved';
} else {
  const userId = req.user_id || req.userId;
  const user = userDatabase.find(u => u.id === userId);
  if (!user) return false;
  const userManagerId = user.manager_id || user.managerId;
  return req.status === 'pending' && userManagerId === currentUser.id;
}
    });
    return filtered;
  }, [currentUser, vacationRequests, userDatabase]);

  const handleRequestSubmit = async (requestData) => {
    const result = await onRequestSubmit(requestData);
    if (result.success) {
      setShowRequestForm(false);
    } else {
      alert(result.error || 'Αποτυχία υποβολής αίτησης');
    }
  };

  const handleRequestDecision = async (requestId, decision, managerLevel) => {
    const result = await onRequestDecision(requestId, decision, managerLevel);
    if (!result.success) {
      alert(result.error || 'Αποτυχία ενημέρωσης αίτησης');
    }
  };

  const handleGrantDays = async (grantData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/grant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userIds: grantData.userIds,
        days: grantData.days,
        startDate: grantData.startDate,
        endDate: grantData.endDate,
        reason: grantData.reason,
        grantedBy: currentUser.id,
        isDeduction: grantData.isDeduction || false,
        leaveType: grantData.leaveType || 'actual'
      }),
    });

      const result = await response.json();
      
      if (result.success) {
        if (onReloadUsers) {
          await onReloadUsers();
        }
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('Grant days failed:', err);
      return { success: false, error: 'Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.' };
    }
  };

  const isViewer = VIEWER_USER_IDS.includes(Number(currentUser.id));
  const isManager = currentUser.role === 'manager';
  const isUserAdmin = USER_ADMIN_IDS.includes(Number(currentUser.id));

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'calendar', label: 'Προβολή Ημερολογίου', icon: Calendar },
      { id: 'requests', label: 'Οι Αιτήσεις μου', icon: Clock },
    ];

    const managerTabs = isManager ? [
      { id: 'team', label: 'Επισκόπηση Αδειών Ομάδας', icon: Users },
      { id: 'manage-days', label: 'Καταχώρηση Άδειας', icon: Gift },
      { id: 'history', label: 'Ιστορικό', icon: History },
      { id: 'annual-view', label: 'Υπόλοιπα Ετών', icon: BarChart3 },
      { id: 'approvals', label: `Εγκρίσεις ${pendingApprovals.length > 0 ? `(${pendingApprovals.length})` : ''}`, icon: Shield },
    ] : [];

    const viewerTabs = (!isManager && isViewer) ? [
      { id: 'team', label: 'Επισκόπηση Αδειών Ομάδας', icon: Users },
	    { id: 'manage-days', label: 'Καταχώρηση Άδειας', icon: Gift },
      { id: 'history', label: 'Ιστορικό', icon: History },
      { id: 'annual-view', label: 'Υπόλοιπα Ετών', icon: BarChart3 },
    ] : [];

    const userAdminTabs = isUserAdmin ? [
      { id: 'admin-tools', label: 'Διαχείριση', icon: UserCog },
    ] : [];

    return [...baseTabs, ...managerTabs, ...viewerTabs, ...userAdminTabs];
  }, [isManager, isViewer, isUserAdmin, pendingApprovals.length]);

  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;

    let scrollTimeout;
    const handleScroll = () => {
      if (isScrolling.current) return;
      
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const containerScrollLeft = container.scrollLeft;
        const containerWidth = container.offsetWidth;
        
        let closestIndex = 0;
        let smallestDistance = Infinity;

        tabRefs.current.forEach((tab, index) => {
          if (!tab) return;
          const tabLeft = tab.offsetLeft;
          const tabCenter = tabLeft + (tab.offsetWidth / 2);
          const containerCenter = containerScrollLeft + (containerWidth / 2);
          const distance = Math.abs(tabCenter - containerCenter);
          
          if (distance < smallestDistance) {
            closestIndex = index;
            smallestDistance = distance;
          }
        });

        const newActiveTab = tabs[closestIndex];
        if (newActiveTab?.id && newActiveTab.id !== activeTab) {
          setActiveTab(newActiveTab.id);
        }
      }, 150);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [activeTab, tabs]);

  const handleTabClick = (id, index) => {
    setActiveTab(id);
    const container = tabContainerRef.current;
    const tab = tabRefs.current[index];
    
    if (container && tab) {
      isScrolling.current = true;
      const containerWidth = container.offsetWidth;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      const scrollPosition = tabLeft - (containerWidth / 2) + (tabWidth / 2);
      
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });

      setTimeout(() => {
        isScrolling.current = false;
      }, 500);
    }
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'calendar':
        return (
          <CalendarView 
            vacationRequests={vacationRequests}
            userDatabase={userDatabase}
            departments={departments}
            currentUser={currentUser}
            holidays={holidays}
            onRequestTimeOff={() => setShowRequestForm(true)}
            onRequestDecision={handleRequestDecision}
            isViewer={isViewer}
          />
        );
      case 'requests':
        return (
          <RequestsView 
            currentUser={currentUser}
            vacationRequests={vacationRequests}
            userDatabase={userDatabase}
            departments={departments}
            holidays={holidays}
            annualBalances={annualBalances}
            selectedLeaveYears={selectedLeaveYears}
            onYearChange={setSelectedLeaveYears}
          />
        );
      case 'team':
        if (!isManager && !isViewer) return null;
        return (
          <TeamView 
            currentUser={currentUser}
            userDatabase={userDatabase}
            vacationRequests={vacationRequests}
            departments={departments}
            holidays={holidays}
            annualBalances={annualBalances}
          />
        );
      case 'manage-days':
  		if (!isManager && !isViewer) return null;
 		return (
    		<ManageDaysView 
      			currentUser={currentUser}
      			userDatabase={userDatabase}
      			departments={departments}
      			vacationRequests={vacationRequests}
      			onGrantDays={handleGrantDays}
      			holidays={holidays}
      			annualBalances={annualBalances}
      			isViewer={isViewer}
    		/>
  		);
      case 'history':
        if (!isManager && !isViewer) return null;
        return (
          <HistoryView
            currentUser={currentUser}
            userDatabase={userDatabase}
            vacationRequests={vacationRequests}
            departments={departments}
            holidays={holidays}
          />
        );
      case 'annual-view':
        if (!isManager && !isViewer) return null;
        return (
          <YearlyBalanceView 
            annualBalances={annualBalances}
            userDatabase={userDatabase} 
            departments={departments}
            vacationRequests={vacationRequests}
            holidays={holidays}
            currentUser={currentUser}
          />
        );
      case 'approvals':
        if (!isManager) return null;
        return (
          <ApprovalsView 
            currentUser={currentUser}
            pendingRequests={pendingApprovals}
            userDatabase={userDatabase}
            departments={departments}
            holidays={holidays}
            annualBalances={annualBalances}
            vacationRequests={vacationRequests}
            onRequestDecision={handleRequestDecision}
          />
        );
      case 'admin-tools':
        if (!isUserAdmin) return null;
        return (
          <AdminToolsView
            currentUser={currentUser}
            departments={departments}
            holidays={holidays}
            onReloadUsers={onReloadUsers}
          />
        );
      default:
        return null;
    }
  };

  const userDepartment = departments.find(dept => dept.id === currentUser.departmentId);

  return (
    <div className="min-h-screen bg-calm-blue-gradient dark:bg-none dark:bg-jewel-bg">
      <div className="container mx-auto p-6 xs:p-4">
        <div className="bg-white dark:bg-jewel-bg rounded-xl shadow-xl overflow-hidden">
          <div className="bg-calm-green-gradient dark:bg-none dark:bg-jewel-surface p-6 xs:p-4 text-white">
            <div className="flex items-center justify-between xs:flex-col xs:gap-4">
              <div className="flex items-center space-x-3">
                <Calendar className="h-8 w-8 xs:h-7 xs:w-7 text-black dark:text-jewel-teal-tint" />
                <div>
                  <h1 className="text-2xl xs:text-lg text-black dark:text-jewel-text font-bold">Ημερολόγιο Αδειών</h1>
                  <p className="opacity-90 text-black dark:text-jewel-text xs:text-sm">Διαχείριση Αδειών.</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 xs:flex-col xs:gap-3 xs:w-full">
                <div className="flex items-center space-x-2 xs:justify-center">
                  {currentUser.role === 'manager' && <Shield className="h-4 w-4 text-black dark:text-jewel-teal-tint" />}
                  <div className="text-right xs:text-center">
                    <p className="font-medium text-black dark:text-jewel-text xs:text-sm xs:font-bold">{currentUser.name}</p>
                    <div className="flex items-center space-x-2 xs:justify-center">
                      <p className="text-xs text-black dark:text-jewel-text opacity-75">
                        {currentUser.role === 'manager' ? 'Διαχειριστής' : 'Υπάλληλος'}
                      </p>
                      {userDepartment && (
                        <>
                          <span className="text-xs text-black dark:text-jewel-text opacity-75">•</span>
                          <div className="flex items-center space-x-1">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: userDepartment.color }}
                            ></div>
                            <p className="text-xs text-black dark:text-jewel-text opacity-75">{userDepartment.name}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onLogout}
                  className="flex items-center border-2 border-black dark:border-jewel-border space-x-2 bg-white/10 dark:bg-jewel-bg/40 hover:bg-white/20 dark:hover:bg-jewel-bg/60 px-3 py-2 rounded-lg transition-colors xs:w-full xs:justify-center"
                >
                  <LogOut className="h-4 w-4 text-black dark:text-jewel-text" />
                  <span className='text-black dark:text-jewel-text'>Αποσύνδεση</span>
                </button>
              </div>
            </div>
          </div>
          <div className="border-b border-gray-200 dark:border-jewel-border">
            <nav
              ref={tabContainerRef}
              className="overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth sm:flex sm:space-x-8 sm:px-6"
              style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
            >
              <div className="flex xs:w-max sm:space-x-8">
                {tabs.map(({ id, label, icon: Icon }, index) => (
                  <button
                    key={id}
                    ref={(el) => (tabRefs.current[index] = el)}
                    onClick={() => handleTabClick(id, index)}
                    style={{ scrollSnapAlign: 'center' }}
                    className={`
                      flex items-center space-x-2 py-4 border-b-2 transition-colors
                      sm:flex-row
                      xs:min-w-[100vw] xs:snap-center xs:flex-col xs:justify-center xs:snap-always
                      ${activeTab === id
                        ? 'border-blue-500 text-blue-600 dark:text-jewel-teal-tint'
                        : 'border-transparent text-gray-500 dark:text-jewel-text-muted'}
                    `}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="xs:text-sm">{label}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>
          <div className="p-6 xs:p-4">
            <UserInfoBar
              currentUser={currentUser}
              usedDays={usedDaysInCurrentCycle}
              remainingDays={remainingDays}
              department={userDepartment}
              vacationRequests={vacationRequests}
              holidays={holidays}
              annualBalances={annualBalances}
              selectedLeaveYears={activeTab === 'requests' && selectedLeaveYears.length > 0
                ? selectedLeaveYears
                : null}
            />
            {renderActiveTab()}
          </div>
        </div>
        {showRequestForm && (
          <RequestModal
            currentUser={currentUser}
            existingRequests={vacationRequests}
            holidays={holidays}
            annualBalances={annualBalances}
            onSubmit={handleRequestSubmit}
            onClose={() => setShowRequestForm(false)}
          />
        )}
      </div>
      <FeedbackWidget currentUser={currentUser} />
    </div>
  );
};

export default Dashboard;